import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { find, get, reduce, endsWith, isEmpty } from "lodash"
import { v4 } from "uuid"
import { getQueryStringState, setQueryStringState } from "helpers/queryString"
import { combineTanStackStatuses, LOADING, OK } from "helpers/data-statuses"
import { usePostQuery } from "./queries"

const EMPTY_OBJECT = {}

function updateSectionState(section, update) {
  if (typeof update === "function") {
    return update(section)
  }

  return {
    ...section,
    ...update,
  }
}

// Use this hook to get the full state from the query string.
export function useQueryString() {
  const location = useLocation()
  // [TODO] update getQueryStringState to take location as parameter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const parsedLocation = useMemo(() => getQueryStringState(), [location])
  return parsedLocation
}

// Use this hook to get the sub-state from the query string
// at the `key` address, and an updater function. Interface
// inspired by the useState hook from react.
export function useQueryStringState(key) {
  const location = useLocation()
  const navigate = useNavigate()
  // [TODO] update getQueryStringState to take location as parameter
  const [section, update] = useMemo(() => {
    const state = getQueryStringState()
    const section = key ? get(state, key, EMPTY_OBJECT) : state

    const updateSection = (update) => {
      let newState
      if (key) {
        newState = {
          ...state,
          [key]: updateSectionState(section, update),
        }
      } else {
        newState = updateSectionState(section, update)
      }

      setQueryStringState(navigate, newState)
    }

    return [section, updateSection]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location, key])

  return [section, update]
}

/**
 * Retrieves preferences and updates the query string state with those values. If there's a conflict with existing query string state, the query string will take precedence.
 * @param {string} accountId - The account ID.
 * @param {string} key - The key of the preference.
 * @param {Object[]} types - The types of preferences to retrieve. Each type object must have a `type` property and an optional `subtypes` array property.
 * @param {function} [mapPreferencesToState] - Optional function to map the preferences object to query string state. Called with the preferences object as the only argument.
 * @returns {string} The status of the preferences query.
 */
export function useInitializeQueryStringWithPreferences(accountId, key, types, mapPreferencesToState) {
  const navigate = useNavigate()
  // We generate a unique ID, as we want to reload preferences everytime we load a screen.
  const ref = useRef(v4())
  // We only want this code to run once, after preferences have been loaded
  const [isInitialized, setIsInitialized] = useState(false)

  const queryResults = usePostQuery({
    url: "/api/users/me/preferences",
    payload: {
      items: getPreferenceItemsPayload(accountId, key, types),
    },
    key: [ref.current],
    enabled: !!ref.current,
    select: (queryResult) => selectPreferenceItems(queryResult, types, mapPreferencesToState),
  })
  const queryStatus = combineTanStackStatuses([queryResults])

  useEffect(() => {
    if (queryStatus === OK) {
      const state = getQueryStringState()
      const newState = {
        ...queryResults.data,
        ...state,
      }
      setQueryStringState(navigate, newState)

      setIsInitialized(true)
    }
  }, [navigate, queryStatus, queryResults.data, mapPreferencesToState])

  // We haven't received the preferences yet
  if (queryStatus !== OK) {
    return queryStatus
  }

  // We received the preferences, but haven't redirected yet
  if (!isInitialized) {
    return LOADING
  }

  return OK
}

function getPreferenceItemsPayload(accountId, key, types) {
  if (isEmpty(types)) {
    return []
  }

  const items = []
  for (const type of types) {
    if (isEmpty(type.subtypes)) {
      items.push({
        accountId,
        key,
        type: type.type,
      })
    } else {
      for (const subtype of type.subtypes) {
        items.push({
          accountId,
          key,
          type: `${type.type}.${subtype}`,
        })
      }
    }
  }

  return items
}

function selectPreferenceItems(queryResult, types, mapPreferences) {
  let preferences = {}

  for (const type of types) {
    if (isEmpty(type.subtypes)) {
      const preference = find(queryResult, (result) => result.type === type.type)
      preferences[type.type] = preference?.value ?? EMPTY_OBJECT
    } else {
      // The order of the subtypes might differ from the order of the queryResult
      preferences = reduce(type.subtypes, (result, subtype) => {
        const preference = find(queryResult, (result) => endsWith(result.type, subtype))
        result[subtype] = get(preference, "value")
        return result
      }, {})
    }
  }
  return mapPreferences ? mapPreferences(preferences) : preferences
}
